import { useEffect } from 'react';
import { fg } from '@atlassian/jira-feature-gating';
import { useFieldValue } from '@atlassian/jira-issue-field-base/src/services/field-value-service/index.tsx';
import { getParentShape } from '@atlassian/jira-issue-field-parent-switcher/src/common/utils.tsx';
import { PARENT_TYPE } from '@atlassian/jira-platform-field-config/src/index.tsx';
import { useDiffCheck } from '../../../../common/hooks/use-diff-check/index.tsx';
import type { Card } from '../../../../model/types.tsx';
import { cardParentIdSet, cardParentSet } from '../../../../state/actions/parent/index.tsx';
import { useBacklogSelector, useBacklogActionCreator } from '../../../../state/index.tsx';
import { issueParentsSelector } from '../../../../state/selectors/issue-parent/index.tsx';

export type Props = {
	card: Card;
};

const FieldSyncParent = ({ card: { key, id, parentId: parentIdFromBacklog } }: Props) => {
	const [parentFromExtractedFields, { setFieldValue: setParentInExtractedFields }] = useFieldValue({
		issueKey: key,
		fieldKey: PARENT_TYPE,
	});

	// parentId in cards is number | null
	const parentIdFromExtractedFields = parentFromExtractedFields?.id
		? Number(parentFromExtractedFields.id)
		: null;

	const parentNameFromExtractedFields = parentFromExtractedFields?.fields?.summary ?? null;
	const parentColorFromExtractedFields = parentFromExtractedFields?.color ?? null;

	const parentIdFromBacklogChanged = useDiffCheck([parentIdFromBacklog]);
	const parentIdFromExtractedFieldsChanged = useDiffCheck([parentIdFromExtractedFields]);

	const storesNeedToSync = parentIdFromBacklog !== parentIdFromExtractedFields;
	const issueParents = useBacklogSelector(issueParentsSelector);
	const setParentIdInBacklog = useBacklogActionCreator(cardParentIdSet);
	const setParentInBacklog = useBacklogActionCreator(cardParentSet);

	useEffect(() => {
		const parentIdFromBacklogIsDefined = parentIdFromBacklog !== undefined;
		// PARENT SYNC: BACKLOG -> EXTRACTED FIELDS
		// initial value, realtime...
		if (parentIdFromBacklogIsDefined && parentIdFromBacklogChanged && storesNeedToSync) {
			const parent =
				issueParents.length &&
				parentIdFromBacklog &&
				issueParents.find((p) => Number(p.id) === Number(parentIdFromBacklog));
			setParentInExtractedFields(
				key,
				PARENT_TYPE,
				parentIdFromBacklogIsDefined && parent
					? getParentShape(parent.id, parent.key, parent.summary, {
							id: parent.issueType?.id,
							name: parent.issueType?.name,
							iconUrl: parent.issueType?.iconUrl ?? '',
						})
					: null,
			);
		}
	}, [
		key,
		storesNeedToSync,
		setParentInExtractedFields,
		parentIdFromBacklog,
		parentIdFromBacklogChanged,
		issueParents,
	]);

	useEffect(() => {
		const parentIdFromExtractedFieldsIsDefined = parentIdFromExtractedFields !== undefined;

		// PARENT SYNC: EXTRACTED FIELDS -> BACKLOG REDUX
		// issue view updates, inline parent edit...
		if (
			// Ensure no sync will be called by Backlog as Backlog is the driver
			!parentIdFromBacklogChanged &&
			parentIdFromExtractedFieldsIsDefined &&
			parentIdFromExtractedFieldsChanged &&
			storesNeedToSync
		) {
			if (fg('jsw-backlog-parent-field-render')) {
				setParentInBacklog(
					id,
					parentFromExtractedFields
						? {
								id: parentFromExtractedFields?.id,
								key: parentFromExtractedFields?.key,
								color: parentFromExtractedFields?.color,
								summary: parentFromExtractedFields?.fields?.summary ?? null,
								isDone: parentFromExtractedFields?.isDone,
							}
						: null,
				);
			} else {
				setParentIdInBacklog(id, parentIdFromExtractedFields);
			}
		}
	}, [
		id,
		storesNeedToSync,
		setParentInBacklog,
		setParentIdInBacklog,
		parentFromExtractedFields,
		parentIdFromExtractedFields,
		parentIdFromBacklogChanged,
		parentIdFromExtractedFieldsChanged,
	]);

	// update epic summary/color if it has been changed in the issue view
	useEffect(() => {
		const parent =
			issueParents.length &&
			parentIdFromExtractedFields &&
			issueParents.find((p) => Number(p.id) === Number(parentIdFromExtractedFields));

		if (
			parent &&
			(parentNameFromExtractedFields !== parent.summary ||
				parentColorFromExtractedFields !== parent.color)
		) {
			setParentInExtractedFields(
				key,
				PARENT_TYPE,
				getParentShape(
					parent.id,
					parent.key,
					parent.summary,
					{
						id: parent.issueType?.id,
						name: parent.issueType?.name,
						iconUrl: parent.issueType?.iconUrl ?? '',
					},
					parent.color,
				),
			);
		}
	}, [
		issueParents,
		key,
		parentColorFromExtractedFields,
		parentIdFromExtractedFields,
		parentNameFromExtractedFields,
		setParentInExtractedFields,
	]);

	return null;
};

export default FieldSyncParent;
